/**
 * The admin pages that CHANGE something, and what it takes to open them.
 *
 * A list or detail page is for looking, and every admin may open the ones the
 * menu shows them. An add / edit page is not: an admin who may only view a
 * module is shown "you can view this, not change it" instead of a form whose
 * save the server would refuse (components/Common/AdminPageGuard).
 *
 * `can` is an action of src/data/capabilities.json; `any` is a list of keys.
 * Paths are matched segment by segment (":x" matches one segment), so
 * "/courses/:id/syllabus" never matches "/courses/:id".
 */
export const EDIT_PAGES = [
  { paths: ["/students/add", "/students/import", "/students/edit/:id"], any: ["add_edit_students"] },
  { paths: ["/students/:id/fees/new", "/students/:id/fees/edit/:feeId", "/fees/new"], can: "fees.collect" },
  { paths: ["/courses/add", "/courses/edit/:courseId"], any: ["add_edit_courses"] },
  { paths: ["/courses/:id/syllabus"], any: ["manage_syllabus", "add_edit_courses"] },
  { paths: ["/batches/add", "/batches/edit/:batchId"], any: ["manage_batches"] },
  { paths: ["/instructors/add", "/instructors/edit/:id"], any: ["add_edit_instructors"] },
  { paths: ["/admins/add", "/admins/edit/:id"], any: ["manage_admins"] },
  { paths: ["/branches/add", "/branches/edit/:branchId"], any: ["manage_branches"] },
  { paths: ["/enquiries/new", "/enquiries/edit/:id"], any: ["add_edit_enquiries"] },
  { paths: ["/enquiry-forms/new", "/enquiry-forms/edit/:id"], can: "enquiryForms.manage" },
  { paths: ["/interviews/new", "/interviews/edit/:id"], can: "interviews.manage" },
  {
    paths: [
      "/assessments/create", "/assessments/edit/:id", "/assignments/edit/:id", "/create-assignment",
      "/batch-assignments/create", "/batch-assignments/edit/:id", "/public-exams/new", "/public-exams/:id/edit",
      "/quiz-bank/add", "/quiz-bank/edit/:id", "/quiz-bank/passages/new",
      "/test-series/create", "/test-series/edit/:id", "/test-series/import",
    ],
    can: "assessments.manage",
  },
  { paths: ["/ai-questions/new", "/ai-questions/edit/:id"], can: "ai.manage" },
  { paths: ["/demo-classes/new", "/demo-classes/:id/edit", "/webinars/new", "/webinars/:id/edit"], can: "liveEvents.manage" },
  { paths: ["/self-paced/add", "/self-paced/edit/:courseId"], can: "selfPaced.manage" },
  { paths: ["/self-paced/certificates/issue"], can: "certificates.issue" },
  { paths: ["/cms/blogs/new", "/cms/blogs/:id"], can: "website.manage" },
  { paths: ["/assets/new", "/assets/:id/edit"], can: "assets.manage" },
  { paths: ["/compliance/mandatory-training/new", "/compliance/mandatory-training/:id"], can: "mandatoryTraining.manage" },
  { paths: ["/compliance/employee-groups/new", "/compliance/employee-groups/:id"], can: "employeeGroups.manage" },
  { paths: ["/marketing/compose", "/marketing/compose/:id"], any: ["manage_campaigns"] },
];

const matches = (pattern, pathname) => {
  const a = pattern.split("/").filter(Boolean);
  const b = pathname.split("/").filter(Boolean);
  return a.length === b.length && a.every((seg, i) => seg.startsWith(":") || seg === b[i]);
};

/** The rule for a path, or null when the page is not an edit page. */
export function pageAccessFor(pathname) {
  const clean = String(pathname || "").split("?")[0].replace(/\/+$/, "") || "/";
  return EDIT_PAGES.find((rule) => rule.paths.some((p) => matches(p, clean))) || null;
}
